import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  LinearProgress,
  Divider
} from '@mui/material';
import {
  Close as CloseIcon,
  TrendingUp as TrendingUpIcon,
  Mouse as ClickIcon,
  CheckCircle as ConversionIcon,
  Percent as PercentIcon,
  CalendarToday as CalendarIcon,
  Person as PersonIcon,
  Link as LinkIcon
} from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import api from '../../../../services/api';

const AffiliateStatsDialog = ({ open, onClose, affiliate }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submissions, setSubmissions] = useState([]);

  useEffect(() => {
    if (open && affiliate) {
      loadAffiliateStats();
    }
  }, [open, affiliate]);

  const loadAffiliateStats = async () => {
    try {
      setLoading(true);
      
      // Carregar estatísticas gerais
      const statsResponse = await api.get(`/affiliates/${affiliate.code}/stats`);
      setStats(statsResponse.data);

      // Carregar submissões recentes (se disponível)
      try {
        const submissionsResponse = await api.get('/forms/submissions', {
          params: {
            affiliateId: affiliate.id,
            limit: 10
          }
        });
        setSubmissions(submissionsResponse.data.data || []);
      } catch (error) {
        console.log('Submissões não disponíveis:', error);
        setSubmissions([]);
      }

    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    try {
      return format(parseISO(date), 'dd/MM/yyyy HH:mm', { locale: ptBR });
    } catch (error) {
      return 'Data inválida';
    }
  };

  if (!affiliate) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center">
            <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Box>
              <Typography variant="h6">
                {affiliate.name}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Código: {affiliate.code}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
            <CircularProgress />
          </Box>
        ) : (
          <Box>
            {/* Cards de estatísticas principais */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={3}>
                <Card sx={{ textAlign: 'center', bgcolor: 'primary.50' }}>
                  <CardContent>
                    <ClickIcon sx={{ fontSize: 32, color: 'primary.main', mb: 1 }} />
                    <Typography variant="h4" color="primary.main">
                      {stats?.clicks || 0}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Total de Clicks
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={3}>
                <Card sx={{ textAlign: 'center', bgcolor: 'success.50' }}>
                  <CardContent>
                    <ConversionIcon sx={{ fontSize: 32, color: 'success.main', mb: 1 }} />
                    <Typography variant="h4" color="success.main">
                      {stats?.conversions || 0}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Conversões
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={3}>
                <Card sx={{ textAlign: 'center', bgcolor: 'info.50' }}>
                  <CardContent>
                    <PercentIcon sx={{ fontSize: 32, color: 'info.main', mb: 1 }} />
                    <Typography variant="h4" color="info.main">
                      {stats?.conversionRate || 0}%
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Taxa de Conversão
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={3}>
                <Card sx={{ textAlign: 'center', bgcolor: 'warning.50' }}>
                  <CardContent>
                    <TrendingUpIcon sx={{ fontSize: 32, color: 'warning.main', mb: 1 }} />
                    <Typography variant="h4" color="warning.main">
                      {affiliate.commissionRate}%
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Comissão
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Barra de progresso da conversão */}
            {stats?.clicks > 0 && (
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Performance de Conversão
                  </Typography>
                  <Box display="flex" alignItems="center" mb={1}>
                    <Typography variant="body2" color="textSecondary" sx={{ minWidth: 120 }}>
                      {stats.conversions} de {stats.clicks} clicks
                    </Typography>
                    <Box sx={{ flexGrow: 1, mx: 2 }}>
                      <LinearProgress 
                        variant="determinate" 
                        value={stats.conversionRate} 
                        sx={{ height: 8, borderRadius: 4 }}
                      />
                    </Box>
                    <Typography variant="body2" color="textSecondary">
                      {stats.conversionRate}%
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            )}

            {/* Informações adicionais */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Informações do Afiliado
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      <strong>Nome:</strong> {affiliate.name}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      <strong>Código:</strong> {affiliate.code}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      <strong>Status:</strong>{' '}
                      <Chip 
                        label={affiliate.active ? 'Ativo' : 'Inativo'} 
                        color={affiliate.active ? 'success' : 'default'}
                        size="small"
                      />
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    {affiliate.email && (
                      <Typography variant="body2" color="textSecondary" gutterBottom>
                        <strong>E-mail:</strong> {affiliate.email}
                      </Typography>
                    )}
                    {affiliate.phone && (
                      <Typography variant="body2" color="textSecondary" gutterBottom>
                        <strong>Telefone:</strong> {affiliate.phone}
                      </Typography>
                    )}
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      <strong>Última atividade:</strong> {formatDate(stats?.lastClick)}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Histórico de atividades recentes */}
            {stats?.lastClick && (
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Atividade Recente
                  </Typography>
                  
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="textSecondary">
                      <CalendarIcon sx={{ fontSize: 16, mr: 1, verticalAlign: 'middle' }} />
                      <strong>Último Click:</strong> {formatDate(stats.lastClick)}
                    </Typography>
                    
                    {stats.lastConversion && (
                      <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                        <ConversionIcon sx={{ fontSize: 16, mr: 1, verticalAlign: 'middle' }} />
                        <strong>Última Conversão:</strong> {formatDate(stats.lastConversion)}
                      </Typography>
                    )}
                  </Box>

                  {submissions.length > 0 && (
                    <Box>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="subtitle2" gutterBottom>
                        Últimas Conversões
                      </Typography>
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Data</TableCell>
                              <TableCell>Nome</TableCell>
                              <TableCell>E-mail</TableCell>
                              <TableCell>Status</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {submissions.slice(0, 5).map((submission, index) => (
                              <TableRow key={index}>
                                <TableCell>
                                  {formatDate(submission.createdAt)}
                                </TableCell>
                                <TableCell>
                                  {submission.data?.name || 'N/A'}
                                </TableCell>
                                <TableCell>
                                  {submission.data?.email || 'N/A'}
                                </TableCell>
                                <TableCell>
                                  <Chip 
                                    label={submission.processed ? 'Processado' : 'Pendente'}
                                    color={submission.processed ? 'success' : 'warning'}
                                    size="small"
                                  />
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  )}
                </CardContent>
              </Card>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AffiliateStatsDialog;